import { Grid, GridItem, Typography } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import type { CountryDropdownItem, DropdownItem, ProfileData } from "libs/codegen/property-onboarding";
import { useWatch } from "react-hook-form";
import styled from "styled-components";
import { DropdownField } from "../form/DropdownField";
import { TextField } from "../form/TextField";
import { useStaticDataAtomValue } from "../staticDataAtom";

const AlignedGrid = styled(Grid)`
  align-items: start;
`;

export const ContactDetailsController = () => {
    const {
        languagesList,
        countriesList,
        cmsData: {
            profile: {
                contactDetails,
                contactDetailsDescription,
                preferredLanguage,
                preferredLanguageRequired,
                countryCode,
                countryCodeRequired,
                phoneNumber,
                phoneNumberRequired,
                email,
            },
        },
    } = useStaticDataAtomValue();
    const countryCodeValue = useWatch<ProfileData>({ name: "countryCode" }) as CountryDropdownItem;
    const emailValue = useWatch<ProfileData>({ name: "email" }) as string;

    return (
        <VStack gap="24">
            <VStack gap="8">
                <Typography variant="heading-default">{contactDetails}</Typography>
                <Typography variant="body-s" textColor="base.subtle">
                    {contactDetailsDescription}
                </Typography>
            </VStack>
            <VStack gap="24" backgroundColor="base.inset" p="16" borderRadius="8">
                <DropdownField<ProfileData>
                    label={preferredLanguage}
                    field="preferredLanguage"
                    items={languagesList}
                    rules={{
                        required: preferredLanguageRequired,
                        validate: {
                            notEmpty: (v) => (v !== null && (v as DropdownItem).id !== 0) || preferredLanguageRequired,
                        },
                    }}
                    dataElementName="preferred-language"
                />
                <AlignedGrid spacing="16">
                    <GridItem sm={12} md={6}>
                        <DropdownField<ProfileData, CountryDropdownItem>
                            label={countryCode}
                            field="countryCode"
                            items={countriesList}
                            itemLabel={(item) => `${item.name} (${item.callingCodePrefix})`}
                            identifyBy="callingCode"
                            rules={{
                                required: countryCodeRequired,
                                validate: {
                                    notEmpty: (v) =>
                                        (v !== null && (v as CountryDropdownItem).callingCode !== 0) ||
                                        countryCodeRequired,
                                },
                            }}
                            dataElementName="contact-country-code"
                        />
                    </GridItem>
                    <GridItem sm={12} md={6}>
                        <TextField<ProfileData>
                            label={phoneNumber}
                            field="phoneNumber"
                            prefix={countryCodeValue?.callingCodePrefix}
                            rules={{
                                required: phoneNumberRequired,
                            }}
                            dataElementName="contact-phone-number"
                        />
                    </GridItem>
                </AlignedGrid>
                <VStack gap="8" p="12" borderRadius="8" backgroundColor="base.background">
                    <Typography variant="body-s" textColor="base.subtle">
                        {email}
                    </Typography>
                    <Typography variant="body-s-strong">{emailValue}</Typography>
                </VStack>
            </VStack>
        </VStack>
    );
};
